import React, { useEffect } from "react";
import { Alert, Button, Drawer, Form, Input, InputNumber, Space, Tag, Typography } from "antd";
import { LinkOutlined, SaveOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Loading from "components/shared-components/Loading";
import { PICK_NOTE_TEXT_MAX_CHAR } from "constants/InputConstant";
import { normaliseGrams } from "functions/productSync";
import { SHOP_COLORS, shopLabel } from "./constants";

const { Text } = Typography;

/**
 * Editing the three warehouse fields of one product.
 *
 * The values are read straight from Firestore when the drawer opens rather than taken from the
 * cached list, and that same snapshot is what the save is checked against - `cms/` is still in
 * use and writes the whole product document, so "what did it say when I opened this" is the
 * only honest basis for a write. Who last touched the product is shown for the same reason:
 * two CMSes on one collection means someone else may be in here too.
 */
const ProductWarehouseDrawer = ({
  open,
  row,
  values,
  meta,
  loading,
  saving,
  canEdit,
  counterpartName,
  onSave,
  onEditSyncedProperties,
  onClose,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    if (!values) {
      form.resetFields();
      return;
    }
    // Stored as a number in some documents and as a numeric string in others; the input needs
    // one type, and a value that is neither (an old typo) must show as empty rather than NaN.
    form.setFieldsValue({
      pickWeightGram: normaliseGrams(values.pickWeightGram),
      volumeWeightGram: normaliseGrams(values.volumeWeightGram),
      pickNote: values.pickNote || "",
    });
  }, [open, values, form]);

  const handleSubmit = async () => {
    const submitted = await form.validateFields();
    onSave(submitted);
  };

  const lastUpdate = () => {
    if (!meta || !meta.updatedAt) return t("product_data.never_updated_here");
    return t("product_data.last_updated", {
      moment: dayjs(meta.updatedAt).format("DD-MM-YYYY HH:mm"),
      user: meta.updatedBy || t("product_data.unknown_user"),
    });
  };

  return (
    <Drawer
      open={open}
      width={520}
      onClose={onClose}
      destroyOnClose
      title={
        !row ? (
          t("product_data.edit_title")
        ) : (
          <Space>
            <Tag color={SHOP_COLORS[row.shopSource] || "default"}>{row.shopSource}</Tag>
            <span>{row.name || row.id}</span>
          </Space>
        )
      }
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={saving}>
            {t("cancel")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!canEdit || loading}
            onClick={handleSubmit}
          >
            {t("save")}
          </Button>
        </Space>
      }
    >
      {!canEdit && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message={t("product_data.no_hvo_session_title")}
          description={t("product_data.no_hvo_session_drawer")}
        />
      )}

      {!!counterpartName && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          icon={<LinkOutlined />}
          message={t("product_data.linked_to", {
            shop: shopLabel(row && row.counterpart && row.counterpart.shopSource),
            product: counterpartName,
          })}
          description={t("product_data.linked_to_description")}
          action={
            <Button size="small" icon={<SettingOutlined />} onClick={onEditSyncedProperties}>
              {t("product_data.synced_properties_button")}
            </Button>
          }
        />
      )}

      {loading ? (
        <Loading cover="content" />
      ) : (
        <>
          <Form form={form} layout="vertical" disabled={!canEdit || saving}>
            <Form.Item
              name="pickWeightGram"
              label={t("product_data.pick_weight_gram")}
              extra={t("product_data.pick_weight_gram_help")}
            >
              <InputNumber
                className="w-100"
                min={0}
                step={100}
                precision={0}
                suffix="gram"
                placeholder={t("product_data.optional")}
              />
            </Form.Item>

            <Form.Item
              name="volumeWeightGram"
              label={t("product_data.volume_weight_gram")}
              extra={t("product_data.volume_weight_gram_help")}
            >
              <InputNumber
                className="w-100"
                min={0}
                step={100}
                precision={0}
                suffix="gram"
                placeholder={t("product_data.optional")}
              />
            </Form.Item>

            <Form.Item
              name="pickNote"
              label={t("product_data.pick_note")}
              extra={t("product_data.pick_note_help")}
            >
              <Input.TextArea
                rows={2}
                maxLength={PICK_NOTE_TEXT_MAX_CHAR}
                showCount
                placeholder={t("product_data.pick_note_placeholder")}
              />
            </Form.Item>
          </Form>

          <Text type="secondary">{lastUpdate()}</Text>
          <br />
          <Text type="secondary">{t("product_data.other_cms_warning")}</Text>
        </>
      )}
    </Drawer>
  );
};

export default ProductWarehouseDrawer;
